import { storage } from "@flatfile/shared-ui";
import { useState, useCallback, useRef, useEffect } from "react";
import { INDEX_ROW_INDEX, INDEX_COLUMN_INDEX, HIDE_TOOLTIPS_CLASS } from "../components/Table/constants.js";
import { useBulkRowSelection } from "../context/useBulkRowSelection.js";
import { useColumns } from "../context/useColumnsContext.js";
import { useColumnSelectionContext } from "../context/useColumnSelectionContext.js";
const disableCellTooltips = (tableId) => {
  const table = document.querySelector(`[data-tableid="${tableId}"]`);
  table == null ? void 0 : table.classList.add(HIDE_TOOLTIPS_CLASS);
};
const enableCellTooltips = (tableId) => {
  const table = document.querySelector(`[data-tableid="${tableId}"]`);
  table == null ? void 0 : table.classList.remove(HIDE_TOOLTIPS_CLASS);
};
const SelectedCellStyle = (theme) => {
  return {
    background: theme.cell.selected.backgroundColor,
    color: ""
  };
};
const SelectedIndexCellStyle = (theme) => {
  return {
    background: theme.indexColumn.selected.backgroundColor,
    color: theme.indexColumn.selected.color
  };
};
const defaultCellStyle = {
  background: "",
  color: ""
};
const rowsAreSelected = (range, totalColumns) => {
  if (!range || totalColumns === void 0)
    return false;
  const firstColumn = range[0][1];
  const lastColumn = range[1][1];
  return firstColumn === INDEX_COLUMN_INDEX && lastColumn === totalColumns - 1 || lastColumn === INDEX_COLUMN_INDEX && firstColumn === totalColumns - 1;
};
const cellsAreEqual = (firstCell, lastCell) => {
  return firstCell[0] === lastCell[0] && firstCell[1] === lastCell[1];
};
const cellIsFocused = (cell, tableId) => {
  const selectedCellRange = getSelectedCellRange({ tableId });
  const rowIndex = cell[0];
  if (!selectedCellRange || rowIndex === INDEX_ROW_INDEX)
    return false;
  const firstCell = selectedCellRange[0];
  return cellsAreEqual(cell, firstCell);
};
const cellIsSelected = (cell, totalColumns, tableId) => {
  const rowIndex = cell[0];
  const columnIndex = cell[1];
  const selectedCellRange = getSelectedCellRange({ tableId, sort: true });
  if (!selectedCellRange || rowIndex === INDEX_ROW_INDEX)
    return false;
  const firstCell = selectedCellRange[0];
  const lastCell = selectedCellRange[1];
  if (!firstCell || !lastCell)
    return false;
  const rowIsInRange = rowIndex >= firstCell[0] && rowIndex <= lastCell[0];
  const columnIsInRange = columnIndex >= firstCell[1] && columnIndex <= lastCell[1];
  if (columnIndex === INDEX_COLUMN_INDEX) {
    return rowIsInRange && rowsAreSelected(selectedCellRange, totalColumns);
  }
  if (cellsAreEqual(firstCell, lastCell) && cellsAreEqual(cell, firstCell)) {
    return false;
  }
  return rowIsInRange && columnIsInRange;
};
const selectedRangeId = (tableId) => {
  if (!tableId)
    return "selectedCellRange";
  return `${tableId}-selectedCellRange`;
};
const isSelectingId = (tableId) => {
  if (!tableId)
    return "isSelecting";
  return `${tableId}-isSelecting`;
};
const getContextMenu = (target) => {
  var _a;
  if (!target)
    return false;
  return target.closest('[role="menu"]') || target.closest("[data-radix-context-menu-content]") || target.closest(".radix-context-menu-content") || ((_a = target.parentElement) == null ? void 0 : _a.getAttribute("data-state")) === "true";
};
const getSelectedCellRange = (options) => {
  const storedCellRange = storage(selectedRangeId(options == null ? void 0 : options.tableId)).getRaw();
  if (!storedCellRange)
    return void 0;
  const parsed = JSON.parse(storedCellRange);
  if (options == null ? void 0 : options.sort) {
    const minX = Math.min(...[parsed[0][0], parsed[1][0]]);
    const minY = Math.min(...[parsed[0][1], parsed[1][1]]);
    const maxX = Math.max(...[parsed[0][0], parsed[1][0]]);
    const maxY = Math.max(...[parsed[0][1], parsed[1][1]]);
    return [
      [minX, minY],
      [maxX, maxY]
    ];
  }
  return parsed;
};
const setSelectedCellRange = (firstCell, lastCell, tableId) => {
  storage(selectedRangeId(tableId)).set([firstCell, lastCell]);
};
const getRowsAreSelected = (totalColumns, tableId) => {
  const range = getSelectedCellRange({ tableId });
  if (!range)
    return false;
  return rowsAreSelected(range, totalColumns);
};
const resetSelectedCellRange = (tableId) => {
  storage(selectedRangeId(tableId)).clear();
};
const getIsSelecting = (tableId) => {
  return !!storage(isSelectingId(tableId)).getRaw();
};
const persistIsSelecting = (selecting, tableId) => {
  if (selecting) {
    storage(isSelectingId(tableId)).setRaw("true");
  } else {
    storage(isSelectingId(tableId)).clear();
    enableCellTooltips(tableId);
  }
};
const styleElements = (elements, anchors, theme) => {
  const { start, end } = anchors;
  const isRowIndexSelected = (start == null ? void 0 : start.x) === (end == null ? void 0 : end.x) && (start == null ? void 0 : start.x) === 0;
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const rect = { x: element.offsetLeft, y: element.offsetTop };
    if (rect.y > 0) {
      if (!start || !end) {
        element.style.background = defaultCellStyle.background;
        element.style.color = defaultCellStyle.color;
      } else {
        const rowIsInRange = isRowIndexSelected || rect.x >= start.x && rect.x <= end.x || rect.x >= end.x && rect.x <= start.x;
        const columnIsInRange = rect.y >= start.y && rect.y <= end.y || rect.y >= end.y && rect.y <= start.y;
        if (rowIsInRange && columnIsInRange) {
          const style = rect.x === 0 ? isRowIndexSelected ? SelectedIndexCellStyle(theme) : defaultCellStyle : SelectedCellStyle(theme);
          element.style.background = style.background;
          element.style.color = style.color;
        } else {
          element.style.background = defaultCellStyle.background;
          element.style.color = defaultCellStyle.color;
        }
      }
    }
  }
};
const useSelectCells = (tableRef, theme, totalColumns, totalRows, tableId, selectedRowPositions) => {
  const { clearColumnSelections } = useColumnSelectionContext();
  const { columnStyles } = useColumns();
  const [isSelecting, setIsSelectingState] = useState(false);
  const setIsSelecting = useCallback(
    (nextIsSelecting) => {
      setIsSelectingState(nextIsSelecting);
      persistIsSelecting(nextIsSelecting, tableId);
    },
    [setIsSelectingState, tableId]
  );
  const LAST_COLUMN_INDEX = totalColumns - 1;
  const LAST_ROW_INDEX = totalRows;
  const rowHeight = theme.rowHeight;
  const anchors = useRef({});
  const selectedRows = useRef(selectedRowPositions ?? []);
  const { getIsBulkSelecting } = useBulkRowSelection();
  const isBulkSelecting = useRef(getIsBulkSelecting == null ? void 0 : getIsBulkSelecting());
  useEffect(() => {
    selectedRows.current = selectedRowPositions ?? [];
  }, [selectedRowPositions]);
  useEffect(() => {
    isBulkSelecting.current = getIsBulkSelecting == null ? void 0 : getIsBulkSelecting();
  }, [getIsBulkSelecting]);
  const resetSelectionState = () => {
    anchors.current = {};
    resetSelectedCellRange(tableId);
    if (!(isBulkSelecting == null ? void 0 : isBulkSelecting.current)) {
      styleCellsWithinRange();
    }
  };
  const clearCellSelection = () => {
    if (getSelectedCellRange({ tableId })) {
      resetSelectionState();
    }
  };
  const styleCellsWithinRange = () => {
    const table = tableRef == null ? void 0 : tableRef.current;
    if (!table)
      return;
    const cellElements = table.querySelectorAll(
      '*[data-cell-wrapper^="BasicCellWrapper"]'
    );
    const indexElements = table.querySelectorAll(
      '*[data-cell-wrapper^="IndexCellWrapper"]'
    );
    styleElements(cellElements, anchors.current, theme);
    styleElements(indexElements, anchors.current, theme);
  };
  const focusCell = (x, y) => {
    const table = tableRef == null ? void 0 : tableRef.current;
    if (!table)
      return;
    const elements = table.querySelectorAll(
      '*[data-cell-wrapper^="BasicCellWrapper"]'
    );
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      if (element.offsetLeft === x && element.offsetTop === y) {
        element.focus();
        return;
      }
    }
  };
  const onCellMouseDown = (cellRef, cell, isRightClick, shiftKey, contextMenuOpen) => {
    var _a, _b;
    if (getContextMenu(cellRef.current)) {
      return;
    }
    if (cell[0] !== INDEX_ROW_INDEX) {
      clearColumnSelections();
    }
    if (shiftKey) {
      setIsSelecting(true);
      onCellMouseEnter(cellRef, cell);
      return;
    }
    if (isRightClick || contextMenuOpen) {
      resetSelectionState();
      setIsSelecting(false);
      return;
    }
    resetSelectionState();
    setIsSelecting(true);
    const x = ((_a = cellRef.current) == null ? void 0 : _a.offsetLeft) || 0;
    const y = ((_b = cellRef.current) == null ? void 0 : _b.offsetTop) || 0;
    anchors.current = {
      start: { x, y },
      end: { x, y }
    };
    if (cell[1] === INDEX_COLUMN_INDEX) {
      setSelectedCellRange(cell, [cell[0], LAST_COLUMN_INDEX], tableId);
      styleCellsWithinRange();
    } else {
      setSelectedCellRange(cell, cell, tableId);
    }
  };
  const onCellMouseEnter = (cellRef, cell) => {
    var _a, _b;
    const selectedCellRange = getSelectedCellRange({ tableId });
    const isSelecting2 = getIsSelecting(tableId);
    if (!isSelecting2 || !selectedCellRange)
      return;
    disableCellTooltips(tableId);
    if (selectedCellRange[0][1] === INDEX_COLUMN_INDEX && cell[1] === INDEX_COLUMN_INDEX) {
      setSelectedCellRange(
        selectedCellRange[0],
        [cell[0], LAST_COLUMN_INDEX],
        tableId
      );
    } else {
      setSelectedCellRange(selectedCellRange[0], cell, tableId);
    }
    anchors.current.end = {
      x: ((_a = cellRef.current) == null ? void 0 : _a.offsetLeft) || 0,
      y: ((_b = cellRef.current) == null ? void 0 : _b.offsetTop) || 0
    };
    styleCellsWithinRange();
  };
  const onCellMouseUp = (cellRef, cell) => {
    const selectedCellRange = getSelectedCellRange({ tableId });
    const isSelecting2 = getIsSelecting(tableId);
    if (!isSelecting2)
      return;
    setIsSelecting(false);
    if (!selectedCellRange)
      return;
    if (selectedCellRange[0][1] === INDEX_COLUMN_INDEX && cell[1] === INDEX_COLUMN_INDEX) {
      setSelectedCellRange(
        selectedCellRange[0],
        [cell[0], LAST_COLUMN_INDEX],
        tableId
      );
    } else {
      setSelectedCellRange(selectedCellRange[0], cell, tableId);
    }
  };
  const onCellFocus = (cellRef, cell, contextMenuOpen) => {
    if (getIsSelecting(tableId) || cellIsFocused(cell, tableId))
      return;
    onCellMouseDown(cellRef, cell, contextMenuOpen || false);
    onCellMouseUp(cellRef, cell);
  };
  const onCellTab = (cellRef, cell, shiftKey) => {
    onCellNavigation(cellRef, cell, "Tab", shiftKey);
  };
  const onCellArrowRight = (cellRef, cell) => {
    const isLastCellInRow = cell[1] === LAST_COLUMN_INDEX;
    if (isLastCellInRow)
      return;
    onCellNavigation(cellRef, cell, "ArrowRight");
  };
  const onCellArrowLeft = (cellRef, cell) => {
    const isFirstCellInRow = cell[1] === 0;
    if (isFirstCellInRow)
      return;
    onCellNavigation(cellRef, cell, "ArrowLeft");
  };
  const onCellArrowUp = (cellRef, cell) => {
    const isFirstRow = cell[0] === 1;
    if (isFirstRow)
      return;
    onCellNavigation(cellRef, cell, "ArrowUp");
  };
  const onCellArrowDown = (cellRef, cell) => {
    const isLastRow = cell[0] === LAST_ROW_INDEX;
    if (isLastRow)
      return;
    onCellNavigation(cellRef, cell, "ArrowDown");
  };
  const onCellNavigation = (cellRef, cell, key, shiftKey) => {
    var _a, _b, _c, _d;
    resetSelectionState();
    setIsSelecting(true);
    const offsetLeft = ((_a = cellRef.current) == null ? void 0 : _a.offsetLeft) || 0;
    const offsetTop = ((_b = cellRef.current) == null ? void 0 : _b.offsetTop) || 0;
    const offsetWidth = ((_c = cellRef.current) == null ? void 0 : _c.offsetWidth) || 0;
    const leftNeighborWidth = ((_d = columnStyles[cell[1] - 1]) == null ? void 0 : _d.width) || 0;
    let x, y, targetCell;
    switch (key) {
      case "ArrowDown":
        x = offsetLeft;
        y = offsetTop + rowHeight;
        targetCell = [cell[0] + 1, cell[1]];
        break;
      case "ArrowUp":
        x = offsetLeft;
        y = offsetTop - rowHeight;
        targetCell = [cell[0] - 1, cell[1]];
        break;
      case "ArrowLeft":
        x = offsetLeft - leftNeighborWidth;
        y = offsetTop;
        targetCell = [cell[0], cell[1] - 1];
        break;
      case "ArrowRight":
        x = offsetLeft + offsetWidth;
        y = offsetTop;
        targetCell = [cell[0], cell[1] + 1];
        break;
      case "Tab":
        if (!shiftKey) {
          const isLastCellInRow = cell[1] === LAST_COLUMN_INDEX;
          const isLastCellInColumn = cell[0] === LAST_ROW_INDEX;
          x = isLastCellInRow ? isLastCellInColumn ? offsetLeft : theme.indexColumn.width : offsetLeft + offsetWidth;
          y = isLastCellInRow ? isLastCellInColumn ? offsetTop : offsetTop + rowHeight : offsetTop;
          targetCell = isLastCellInRow ? isLastCellInColumn ? cell : [cell[0] + 1, 0] : [cell[0], cell[1] + 1];
        } else {
          const isFirstCellInRow = cell[1] === 0;
          const isFirstCellInColumn = cell[0] === 1;
          x = isFirstCellInRow ? isFirstCellInColumn ? offsetLeft : theme.indexColumn.width + LAST_COLUMN_INDEX * theme.column.width : offsetLeft - offsetWidth;
          y = isFirstCellInRow ? isFirstCellInColumn ? offsetTop : offsetTop - rowHeight : offsetTop;
          targetCell = isFirstCellInRow ? isFirstCellInColumn ? cell : [cell[0] - 1, LAST_COLUMN_INDEX] : [cell[0], cell[1] - 1];
        }
        break;
    }
    if (x !== void 0 && y !== void 0 && targetCell) {
      anchors.current = {
        start: { x, y },
        end: { x, y }
      };
      focusCell(x, y);
      setSelectedCellRange(targetCell, targetCell, tableId);
      setIsSelecting(false);
    }
  };
  const onSelectRows = (rows) => {
    if (!rows.length) {
      resetSelectionState();
      return;
    }
    const startRowIndex = rows[0] + 1;
    const endRowIndex = rows[rows.length - 1] + 1;
    anchors.current = {
      start: { x: 0, y: startRowIndex * rowHeight },
      end: { x: 0, y: endRowIndex * rowHeight }
    };
    setSelectedCellRange(
      [startRowIndex, INDEX_COLUMN_INDEX],
      [endRowIndex, LAST_COLUMN_INDEX],
      tableId
    );
    styleCellsWithinRange();
  };
  const onDocumentMouseUp = (event) => {
    if (event.button === 2 || getContextMenu(event.target)) {
      return;
    }
    if (selectedRows.current.length > 0) {
      return;
    }
    if (!tableRef.current) {
      return;
    }
    const target = event == null ? void 0 : event.target;
    if (target && target.className === "cell") {
      return;
    }
    if (tableRef.current.contains(target)) {
      return;
    }
    const selectedCellRange = getSelectedCellRange({ tableId });
    if (!selectedCellRange)
      return;
    resetSelectionState();
    setIsSelecting(false);
  };
  useEffect(() => {
    setIsSelecting(false);
    resetSelectionState();
    document == null ? void 0 : document.addEventListener("mouseup", onDocumentMouseUp);
    return () => {
      setIsSelecting(false);
      resetSelectionState();
      document == null ? void 0 : document.removeEventListener("mouseup", onDocumentMouseUp);
    };
  }, []);
  return {
    clearCellSelection,
    isSelecting,
    setIsSelecting,
    onCellMouseDown,
    onCellMouseEnter,
    onCellMouseUp,
    onCellFocus,
    onCellTab,
    onCellArrowRight,
    onCellArrowLeft,
    onCellArrowUp,
    onCellArrowDown,
    onSelectRows,
    onDocumentMouseUp
  };
};
export {
  SelectedCellStyle,
  SelectedIndexCellStyle,
  cellIsFocused,
  cellIsSelected,
  cellsAreEqual,
  getContextMenu,
  getIsSelecting,
  getRowsAreSelected,
  getSelectedCellRange,
  persistIsSelecting,
  resetSelectedCellRange,
  rowsAreSelected,
  setSelectedCellRange,
  styleElements,
  useSelectCells
};
